import {
  ChangeDetectorRef,
  Directive,
  effect,
  inject,
  input,
} from '@angular/core';

import { SuiButtonComponent } from '@sensika/sui/button';
import { SuiTooltipDirective } from '@sensika/sui/tooltip';

import { DataGridComponent } from './data-grid.component';

/**
 * A labelled `sui-button` projected into `<app-data-grid>`'s `[toolbarActions]`
 * slot. Mirrors the toolbar collapse: icon-only with the label as tooltip while
 * collapsed, the label rendered while expanded. A host tooltip
 * (`gridToolbarActionTooltip`, e.g. the limit-reached reason on a disabled
 * Create) wins in both states. The tooltip directive is attached here - never
 * add `[suiTooltip]` alongside. Reaches the grid through DI, so the button must
 * sit inside the `<app-data-grid>` element of the host template.
 */
@Directive({
  selector: 'sui-button[gridToolbarAction]',
  standalone: false,
  hostDirectives: [SuiTooltipDirective],
})
export class GridToolbarActionDirective {
  /** The same `[label]` binding `sui-button` receives; tracked here for the tooltip. */
  readonly label = input<string | undefined>();
  readonly tooltip = input<string | undefined>(undefined, {
    alias: 'gridToolbarActionTooltip',
  });

  private readonly grid = inject(DataGridComponent);
  private readonly button = inject(SuiButtonComponent, { self: true });
  private readonly buttonTooltip = inject(SuiTooltipDirective, { self: true });
  // On a component host element this is the component's own (OnPush) view.
  private readonly buttonCdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      const collapsed = this.grid.toolbarCollapsed();
      this.button.iconOnly = collapsed;
      this.buttonTooltip.content =
        this.tooltip() ?? (collapsed ? this.label() : '');
      this.buttonCdr.markForCheck();
    });
  }
}
